/**
 * Chargement paresseux de face-api (détection du visage sur le selfie KYC).
 *
 * Le bundle `face-api.min.js` embarque déjà TensorFlow.js : charger @tensorflow/tfjs
 * à côté doublait le téléchargement (~1,2 Mo inutiles) et faisait cohabiter deux
 * versions de tfjs sur la page, avec des conflits d'enregistrement de backend.
 */
const FACE_API_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.8/dist/face-api.min.js';
export const FACE_API_MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.8/model/';

export const SELFIE_FACE_MESSAGES = {
  none: 'Aucun visage détecté. Prenez un selfie de face, visage centré et bien éclairé.',
  multiple: 'Un seul visage doit être visible sur la photo (selfie personnel, sans autre personne).',
  invalid: 'La photo doit montrer clairement votre visage seul, de face.',
  tooSmall: 'Votre visage est trop petit dans l\'image. Rapprochez-vous de la caméra.',
  blurry: 'La photo est trop floue. Restez immobile et assurez-vous d\'un bon éclairage.',
  tooDark: 'L\'image de la caméra est trop sombre. Vérifiez l\'éclairage et réessayez.',
};

const SCRIPT_TIMEOUT_MS = 20000;
const MODELS_TIMEOUT_MS = 30000;

/** Promesse unique partagée : les appels concurrents attendent le même chargement. */
let readyPromise = null;

function withTimeout(promise, ms, message) {
  let timer;
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === '1') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = '1';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function loadFaceApi() {
  await withTimeout(loadScript(FACE_API_URL), SCRIPT_TIMEOUT_MS, 'Timeout chargement face-api');

  if (typeof faceapi === 'undefined') {
    throw new Error('face-api indisponible');
  }

  await withTimeout(
    faceapi.nets.tinyFaceDetector.loadFromUri(FACE_API_MODEL_URL),
    MODELS_TIMEOUT_MS,
    'Timeout modèles face-api'
  );

  return true;
}

/**
 * Charge face-api + le modèle TinyFaceDetector, une seule fois par session.
 * Un échec réinitialise le cache pour qu'une nouvelle tentative soit possible.
 */
export function ensureFaceApiReady() {
  if (!readyPromise) {
    readyPromise = loadFaceApi().catch((err) => {
      readyPromise = null;
      throw err;
    });
  }
  return readyPromise;
}
